import type {
  LongMonthCommand,
  LongPolicy,
  LongPricingRead,
  LongRule,
  LongRuleType,
  LongRuleValues,
} from '@/api/core/native-long-month-pricing';
import { OptionSelect } from '@/pages/settings/option-select';
import { Button, DatePicker, Input } from '@mgrvtechgroup/admin-ds';
import { useState } from 'react';

const DAY_MS = 86_400_000;
const KST_MS = 9 * 3_600_000;
const NO_TIER = 'none';
const TYPE_LABEL: Record<LongRuleType, string> = {
  AMOUNT_DISCOUNT: '정액 할인(원)',
  RATE_DISCOUNT: '비율 할인(%)',
};
const APPLIES: Record<'NEW' | 'EXTENSION', string> = { NEW: '신규 계약', EXTENSION: '연장' };

const dateText = (value: Date | undefined) =>
  value
    ? `${String(value.getFullYear())}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    : '';
const dateObject = (value: string) => (value ? new Date(`${value}T12:00:00`) : undefined);
/** 한국 시간 하루 단위 접수 기간만 날짜 선택기로 다룬다. 접수 하한은 그날 0시, 상한은 마지막 접수일 다음 날 0시(미포함)이다. */
const kstMidnight = (date: string, plusDays = 0) => {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) + plusDays));
  return `${next.toISOString().slice(0, 10)}T00:00:00+09:00`;
};
const kstDay = (instant: string, minusDays = 0): string | null => {
  const ms = Date.parse(instant);
  if (Number.isNaN(ms)) return null;
  const shifted = ms + KST_MS - minusDays * DAY_MS;
  return (ms + KST_MS) % DAY_MS === 0 ? new Date(shifted).toISOString().slice(0, 10) : null;
};
const decimal = /^\d{1,18}(\.\d{1,6})?$/;
const integer = /^[1-9]\d{0,14}$/;

interface Draft {
  summary: string;
  build: (reason: string) => LongMonthCommand;
}

function describe(rule: LongRule, read: LongPricingRead) {
  const tier = read.tiers.find((t) => t.id === rule.term_tier_id);
  const from = rule.booking_from ? (kstDay(rule.booking_from) ?? rule.booking_from) : null;
  const until = rule.booking_until ? (kstDay(rule.booking_until, 1) ?? rule.booking_until) : null;
  const value =
    rule.calculation_type === 'AMOUNT_DISCOUNT'
      ? `정액 ${rule.calculation_value.toLocaleString('ko-KR')}원 할인`
      : rule.calculation_type === 'RATE_DISCOUNT'
        ? `비율 ${String(rule.calculation_value)}% 할인`
        : '이 화면에서 다루지 않는 계산 방식';
  return `${tier ? `구간 ${tier.code}(${String(tier.min_duration)}~${String(tier.max_duration)}${tier.duration_unit === 'MONTH' ? '개월' : '일'})` : '구간 조건 없음'} · ${APPLIES[rule.applies_to]} · ${value} · 접수 ${from ?? '제한 없음'} ~ ${until ?? '제한 없음'}`;
}

/** 직접 설정한 월 가격 정책의 할인 규칙과 정책 해제. 계산은 서버가 하며, 화면은 서버가 준 전체 비교값(CAS)을 그대로 싣는다. */
export function LongMonthRulesPanel({
  read,
  own,
  busy,
  pending,
  submit,
}: {
  read: LongPricingRead;
  own: LongPolicy | null;
  busy: boolean;
  /** 결과를 모르는 요청이 남아 있으면 새 요청을 만들지 않는다. */
  pending: boolean;
  submit: (command: LongMonthCommand) => void;
}) {
  const [form, setForm] = useState<{ ruleId: string | null } | null>(null);
  const [tierId, setTierId] = useState(NO_TIER);
  const [applies, setApplies] = useState<'NEW' | 'EXTENSION'>('NEW');
  const [type, setType] = useState<LongRuleType>('AMOUNT_DISCOUNT');
  const [value, setValue] = useState('');
  const [from, setFrom] = useState('');
  const [last, setLast] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [reason, setReason] = useState('');
  const locked = busy || pending;

  if (!own)
    return (
      <p className="body-sm text-muted-foreground">
        할인 규칙은 이 대상에 직접 설정한 월 가격이 있을 때만 둘 수 있습니다.
      </p>
    );
  const policy = own;
  const rules = read.rules.filter((r) => r.price_policy_id === policy.id && r.deleted_at === null);
  const tiers = read.tiers.filter((t) => t.deleted_at === null);
  const expected = read.cas;
  const base = (why: string) => ({ command_id: crypto.randomUUID(), reason: why, expected });

  const open = (rule: LongRule | null) => {
    setForm({ ruleId: rule?.id ?? null });
    setTierId(rule?.term_tier_id ?? NO_TIER);
    setApplies(rule?.applies_to ?? 'NEW');
    setType(
      rule && rule.calculation_type !== 'FIXED_PRICE' ? rule.calculation_type : 'AMOUNT_DISCOUNT',
    );
    setValue(rule ? String(rule.calculation_value) : '');
    setFrom(rule?.booking_from ? (kstDay(rule.booking_from) ?? '') : '');
    setLast(rule?.booking_until ? (kstDay(rule.booking_until, 1) ?? '') : '');
    setDraft(null);
    setReason('');
  };
  const editable = (rule: LongRule) =>
    rule.calculation_type !== 'FIXED_PRICE' &&
    (rule.booking_from === null || kstDay(rule.booking_from) !== null) &&
    (rule.booking_until === null || kstDay(rule.booking_until, 1) !== null);
  const valueError =
    value === ''
      ? ''
      : type === 'AMOUNT_DISCOUNT'
        ? integer.test(value)
          ? ''
          : '정액은 1 이상의 정수(원)로 입력해 주세요.'
        : decimal.test(value) && Number(value) > 0
          ? ''
          : '비율은 0보다 크고 소수 6자리 이하의 숫자로 입력해 주세요.';
  const windowError = from && last && from > last ? '마지막 접수일이 시작일보다 빠릅니다.' : '';
  const review = () => {
    if (!form || !value || valueError || windowError) return;
    const ruleId = form.ruleId;
    const values: LongRuleValues = {
      term_tier_id: tierId === NO_TIER ? null : tierId,
      applies_to: applies,
      booking_from: from ? kstMidnight(from) : null,
      booking_until: last ? kstMidnight(last, 1) : null,
      calculation_type: type,
      // 정액은 JSON 정수, 비율은 입력한 십진 문자열 그대로 보낸다.
      calculation_value: type === 'AMOUNT_DISCOUNT' ? Number(value) : value,
    };
    setDraft({
      summary: `${ruleId ? '할인 규칙 수정' : '할인 규칙 추가'}: ${TYPE_LABEL[type]} ${value}`,
      build: (why) =>
        ruleId
          ? { op: 'rule-put', policyId: policy.id, ruleId, body: { ...base(why), rule: values } }
          : { op: 'rule-create', policyId: policy.id, body: { ...base(why), rule: values } },
    });
  };

  return (
    <div className="body-sm space-y-4 border-t pt-4 leading-relaxed" aria-label="월 가격 할인 규칙">
      <h4 className="font-medium">할인 규칙</h4>
      <p className="body-sm text-muted-foreground">
        정액·비율 할인만 다룹니다. 보증금과 청소비는 할인되지 않습니다. 실제로 적용되는 금액은 위의
        서버 계산으로 확인하세요.
      </p>
      <ul aria-label="현재 할인 규칙" className="body-sm space-y-2">
        {rules.length === 0 && <li>직접 설정한 할인 규칙이 없습니다.</li>}
        {rules.map((rule) => (
          <li key={rule.id} className="space-y-1">
            <span>{describe(rule, read)}</span>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                className="h-auto min-h-8 whitespace-normal"
                variant="outline"
                disabled={locked || !editable(rule)}
                onClick={() => {
                  open(rule);
                }}
              >
                규칙 수정
              </Button>
              <Button
                className="h-auto min-h-8 whitespace-normal"
                variant="outline"
                disabled={locked}
                onClick={() => {
                  setForm(null);
                  setReason('');
                  setDraft({
                    summary: `할인 규칙 삭제: ${describe(rule, read)}`,
                    build: (why) => ({
                      op: 'rule-retire',
                      policyId: policy.id,
                      ruleId: rule.id,
                      body: base(why),
                    }),
                  });
                }}
              >
                규칙 삭제
              </Button>
            </div>
            {!editable(rule) && (
              <span className="text-muted-foreground block">
                접수 시각이 하루 단위가 아니거나 다른 계산 방식이라 이 화면에서는 수정할 수
                없습니다.
              </span>
            )}
          </li>
        ))}
      </ul>
      {!form && !draft && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          disabled={locked}
          onClick={() => {
            open(null);
          }}
        >
          할인 규칙 추가
        </Button>
      )}
      {form && !draft && (
        <div className="space-y-3" aria-label="할인 규칙 입력">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1">
              <span className="body-sm">계약 구간 조건</span>
              <OptionSelect
                aria-label="계약 구간 조건"
                value={tierId}
                options={[
                  { value: NO_TIER, label: '구간 조건 없음' },
                  ...tiers.map((t) => ({
                    value: t.id,
                    label: `구간 ${t.code} (${String(t.min_duration)}~${String(t.max_duration)}${t.duration_unit === 'MONTH' ? '개월' : '일'})`,
                  })),
                ]}
                onValueChange={setTierId}
              />
            </div>
            <div className="grid gap-1">
              <span className="body-sm">적용 대상</span>
              <OptionSelect
                aria-label="적용 대상"
                value={applies}
                options={[
                  { value: 'NEW', label: APPLIES.NEW },
                  { value: 'EXTENSION', label: APPLIES.EXTENSION },
                ]}
                onValueChange={(next) => {
                  setApplies(next as 'NEW' | 'EXTENSION');
                }}
              />
            </div>
            <div className="grid gap-1">
              <span className="body-sm">계산 방식</span>
              <OptionSelect
                aria-label="계산 방식"
                value={type}
                options={[
                  { value: 'AMOUNT_DISCOUNT', label: TYPE_LABEL.AMOUNT_DISCOUNT },
                  { value: 'RATE_DISCOUNT', label: TYPE_LABEL.RATE_DISCOUNT },
                ]}
                onValueChange={(next) => {
                  setType(next as LongRuleType);
                  setValue('');
                }}
              />
            </div>
            <label className="grid gap-1">
              <span className="body-sm">할인 값 ({type === 'AMOUNT_DISCOUNT' ? '원' : '%'})</span>
              <Input
                inputMode="decimal"
                value={value}
                aria-invalid={!!valueError}
                onChange={(e) => {
                  setValue(e.target.value.trim());
                }}
              />
            </label>
            <div className="body-sm grid gap-1">
              접수 시작일 (한국 시간, 비우면 제한 없음)
              <DatePicker
                value={dateObject(from)}
                onChange={(date) => {
                  setFrom(dateText(date));
                }}
              />
            </div>
            <div className="body-sm grid gap-1">
              마지막 접수일 (그날 끝까지, 비우면 제한 없음)
              <DatePicker
                value={dateObject(last)}
                onChange={(date) => {
                  setLast(dateText(date));
                }}
              />
            </div>
          </div>
          {(valueError || windowError) && (
            <p role="alert" className="body-sm">
              {valueError || windowError}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              className="h-auto min-h-8 whitespace-normal"
              variant="outline"
              disabled={!value || !!valueError || !!windowError}
              onClick={review}
            >
              규칙 내용 확인
            </Button>
            <Button
              className="h-auto min-h-8 whitespace-normal"
              variant="outline"
              onClick={() => {
                setForm(null);
              }}
            >
              규칙 입력 취소
            </Button>
          </div>
        </div>
      )}
      {draft && (
        <div className="space-y-2" aria-label="할인 규칙 변경 확인">
          <p className="body-sm" role="status">
            {draft.summary}
          </p>
          <label className="grid gap-1">
            <span className="body-sm">변경 사유</span>
            <Input
              value={reason}
              disabled={locked}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              className="h-auto min-h-8 whitespace-normal"
              disabled={locked || !reason.trim()}
              onClick={() => {
                submit(draft.build(reason.trim()));
              }}
            >
              할인 규칙 변경 저장
            </Button>
            <Button
              className="h-auto min-h-8 whitespace-normal"
              variant="outline"
              disabled={locked}
              onClick={() => {
                setDraft(null);
                setForm(null);
              }}
            >
              취소
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2 border-t pt-3" aria-label="월 가격 정책 해제">
        <p className="body-sm text-muted-foreground">
          정책 해제는 항목 하나를 비우는 것과 다릅니다. 이 대상에 직접 설정한 월 가격 전체를
          걷어내고 상위 단계 값을 따르게 합니다.
        </p>
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          disabled={locked}
          onClick={() => {
            setForm(null);
            setReason('');
            setDraft({
              summary: '직접 설정한 월 가격 정책 전체 해제',
              build: (why) => ({ op: 'retire-policy', policyId: policy.id, body: base(why) }),
            });
          }}
        >
          직접 설정한 월 가격 전체 해제
        </Button>
      </div>
    </div>
  );
}
